import { useSession } from "@supabase/auth-helpers-react";
import { ItemType } from "antd/es/menu/interface";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MenuItem } from "@/components/layouts/MenuItem";
import { useSidebar } from "@/components/layouts/SidebarContext";
import { getAllTools, getToolsAsObject } from "@/features/branding/tools";
import { Tool } from "@/types/tools";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { Badge } from "antd";
export interface ToolMenu {
  items: ItemType[];
  selectedKey: string;
  onClick: (key: string) => void;
}
export const useToolMenu = (): ToolMenu => {
  const router = useRouter();
  const {
    t
  } = useTranslation();
  const session = useSession();
  const {
    isSidebarCollapsed
  } = useSidebar();
  const {
    portfolioAutoAdaptabilityEnabled
  } = useAdaptationPermissions();
  const [selectedKey, setSelectedKey] = useState<string>(router.asPath.split("?")[0]);
  const [items, setItems] = useState<ItemType[]>([]);
  useEffect(() => {
    setSelectedKey(router.asPath.split("?")[0]);
  }, [router.asPath]);
  const rebuildTools = async () => {
    const allTools = await getToolsAsObject(session);
    const menuItems: ItemType[] = Object.keys(allTools).reduce<ItemType[]>((acc, _groupId) => {
      const groupId = _groupId as keyof typeof allTools;
      const group = allTools[groupId];
      if (!allTools[groupId].length || groupId === "create") {
        return acc;
      }
      return [...acc, {
        key: groupId,
        label: t(`tools:${groupId}.title`),
        type: "group",
        children: group.map((item: Tool) => {
          // Async buildSidebarRedirect implementations are always external —
          // the only reason to defer URL generation is a deep-link handshake
          // with another origin (e.g. Document360 SSO).
          const sidebarUrl = item.buildSidebarRedirect?.();
          const isExternal = typeof sidebarUrl === "string" ? sidebarUrl.startsWith("http") : !!sidebarUrl;
          const toolLabel = item.overrides?.menuLabel ?? item.overrides?.title ?? t(`tools:${item.keyPrefix}.title`);
          const isSelected = selectedKey === `/tools/${groupId}/${item.id}`;
          const showUpdateBadge = portfolioAutoAdaptabilityEnabled && item.keyPrefix === "comprehensive.portfolioInsights" || item.keyPrefix === "comprehensive.severities";
          const icon = showUpdateBadge && isSidebarCollapsed ? <span className="relative inline-flex">
                  <Badge dot>{item.icon}</Badge>
                </span> : item.icon;
          return {
            key: `/tools/${groupId}/${item.id}`,
            icon,
            title: showUpdateBadge ? `${toolLabel} (Update available)` : toolLabel as string,
            label: <MenuItem toolLabel={toolLabel as string} isExternal={!!isExternal} selected={isSelected} badgeLabel={showUpdateBadge ? "Update available" : undefined} />
          };
        })
      }];
    }, []);
    setItems(menuItems);
  };
  useEffect(() => {
    rebuildTools();
  }, [session, selectedKey, isSidebarCollapsed, portfolioAutoAdaptabilityEnabled]);
  const onClick = async (key: string) => {
    const tool = getAllTools().find(({
      category,
      id
    }) => `/tools/${category}/${id}` === key);

    // Sidebar redirect beats all.
    if (tool && tool.buildSidebarRedirect) {
      const url = await tool.buildSidebarRedirect(session?.access_token);
      if (url.startsWith("http")) {
        window.open(url, "_blank");
      } else {
        router.push(url);
      }
      return;
    }
    if (key !== router.pathname) {
      setSelectedKey(key);
      router.push(key);
    }
  };
  return {
    items,
    selectedKey,
    onClick
  };
};